import { dateUtils } from '@festina-life/core'
import { Alert } from '@festina-life/ui/feedback'
import { useTranslate } from '@festina-life/ui/hooks'
import { useCallback, useMemo } from 'react'

import { useTemplateContext } from '../../../context/templateContext'
import { useComparableField } from '../../../context/useComparableField'
import type { TemplateVersion } from '../../../types/templateModel'
import { useOperationsContext } from '../../../workspace/operationsContext'
import { stringifyComparableValue } from '../../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../../diffIndicators/ValueSetupDiffIndicators'
import { DocumentationEntry } from '../../documentationEntry/DocumentationEntry'
import { TemplateDocumentationEntry } from '../../documentationEntry/TemplateDocumentationEntry'
import { ConfigurationMarkdownViewer } from '../../markdownviewer/ConfigurationMarkdownViewer'
import { TemplateVersionDocumentationEditor } from '../../templateDocumentationEditors/TemplateVersionDocumentationEditor'
import { isTemplateVersionValidationError } from '../../templateFieldsTable/fieldsHelpers'
import { TemplateVersionName } from '../../templateVersionName/TemplateVersionName'
import { TemplateVersionParent } from '../../templateVersionParent/TemplateVersionParent'
import { TemplateVersionReplaces as TemplateVersionReplacesParent } from '../../templateVersionReplacesParent/TemplateVersionReplaces'

type Props = {
  isExpanded?: boolean
  templateVersion: TemplateVersion
}

export const TemplateVersionContent = ({
  templateVersion,
  isExpanded,
}: Props) => {
  const translate = useTranslate()
  const { isDraftTemplate } = useTemplateContext()
  const { errors } = useOperationsContext()
  const templateErrors = useMemo(
    () =>
      errors.filter(({ error }) =>
        isTemplateVersionValidationError(error, templateVersion.versionInfo),
      ),
    [errors, templateVersion.versionInfo],
  )

  const {
    versionInfo: { fromDate },
    typeName,
    modelType,
  } = templateVersion

  const comparableField = useComparableField<TemplateVersion>(
    templateVersion.path,
  )

  const replacesValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<TemplateVersion>) =>
      comparable?.replaces ? stringifyComparableValue(comparable.replaces) : '',
    [],
  )

  const documentationValueFromComparable = useCallback(
    ({ value: comparable }: SerialiseValueParams<TemplateVersion>) =>
      comparable?.documentation
        ? stringifyComparableValue(comparable.documentation)
        : '',
    [],
  )

  return (
    <>
      {!!templateErrors.length && (
        <div className="col-span-2 flex flex-col gap-y-2">
          {templateErrors.map(({ error }) => (
            <Alert
              severity="error"
              key={error.errorMessage ?? ''}
              title={error.errorMessage ?? ''}
            />
          ))}
        </div>
      )}
      <DocumentationEntry
        title={translate('templateViewer.templateDetails.name')}
        value={<TemplateVersionName isExpanded={!!isExpanded} />}
      />
      <DocumentationEntry
        title={translate('templateViewer.templateDetails.parent')}
        value={
          <TemplateVersionParent isEditable={isExpanded && isDraftTemplate} />
        }
      />
      <TemplateDocumentationEntry
        title={translate('templateViewer.templateDetails.replaces')}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={replacesValueFromComparable}
            compareFilter="replaces"
          />
        }
        value={
          <TemplateVersionReplacesParent
            isEditable={isExpanded && isDraftTemplate}
          />
        }
        origin={null}
        path={templateVersion.path}
      />
      <DocumentationEntry
        title={translate('templateViewer.templateDetails.startDate')}
        value={dateUtils.dateFormatter(fromDate)}
      />
      <TemplateDocumentationEntry
        title={translate(
          'templateViewer.templateDetails.templateDocumentation',
        )}
        diffIndicator={
          <ValueSetupDiffIndicators
            comparableValues={comparableField}
            serialiseValueFn={documentationValueFromComparable}
            compareFilter="documentation"
          />
        }
        value={
          <TemplateVersionDocumentationEditor
            templateVersion={templateVersion}
          />
        }
        origin={templateVersion.documentation?.origin}
        path={`${templateVersion.path}.documentation`}
      />
      <DocumentationEntry
        title={translate('templateViewer.templateDetails.templateType')}
        value={typeName}
      />
      <DocumentationEntry
        title={translate('templateViewer.templateDetails.modelType')}
        value={modelType?.typeName || '-'}
      />
      <DocumentationEntry
        title={translate(
          'templateViewer.templateDetails.modelTypeDocumentation',
        )}
        value={
          <ConfigurationMarkdownViewer shouldTransformLinksToDocSite>
            {modelType?.documentation ?? '\\-'}
          </ConfigurationMarkdownViewer>
        }
      />
    </>
  )
}
